import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { isTestingModeEnabled } from "@/integrations/supabase/client";
import TestEmailViewer from "@/components/auth/TestEmailViewer";
import AuthCard from "@/components/auth/AuthCard";
import SignInForm from "@/components/auth/SignInForm";
import SignUpForm from "@/components/auth/SignUpForm";
import SMSSignUpForm from "@/components/auth/SMSSignUpForm";
import PhoneSignUpForm from "@/components/auth/PhoneSignUpForm";
import ForgotPasswordForm from "@/components/auth/ForgotPasswordForm";
import ResetPasswordForm from "@/components/auth/ResetPasswordForm";
import { useAuthVerificationContext } from "@/contexts/AuthVerificationContext";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { SafeAuthVerificationTracker } from "@/components/auth/SafeAuthVerificationTracker";
import { authNavigationController, AuthFlowStage } from "@/controllers/AuthNavigationController";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useSMSSignUp } from "@/hooks/auth/useSMSSignUp";

type AuthViewType = "signIn" | "signUp" | "smsSignUp" | "phoneSignUp" | "forgotPassword" | "resetPassword";

const Auth = () => {
  const [activeView, setActiveView] = useState<AuthViewType>("signIn");
  const [testingMode, setTestingMode] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [showAccessTokenDialog, setShowAccessTokenDialog] = useState(false);
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Temporarily disabled to prevent verification toasts during normal login
  // const { startVerificationSession, recordVerificationStep } = useAuthVerificationContext();
  
  const { 
    isLoading, 
    isAuthenticated, 
    signIn, 
    signUp,
    resetPassword,
    updatePassword,
    authStatus
  } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [validationError, setValidationError] = useState("");
  const [isRecoveryFlow, setIsRecoveryFlow] = useState(false);

  // SMS signup hook
  const { handleSMSSignUp, loading: smsLoading, validationError: smsValidationError, setValidationError: setSMSValidationError } = useSMSSignUp();
  
  // Check if this is a legacy token-based SMS invitation (for backwards compatibility)
  const invitationToken = searchParams.get('token');
  const isSecureInvitation = !!invitationToken;

  // Temporarily disabled to prevent context warnings and conflicting toasts
  // useEmailVerification();

  useEffect(() => {
    authNavigationController.setNavigate(navigate);
  }, [navigate]);

  useEffect(() => {
    const checkForAuthEvents = async () => {
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        console.log(`[Auth] Detected auth event: ${event}, session: ${session ? 'exists' : 'none'}`);
        
        if (event === 'PASSWORD_RECOVERY' && session) {
          console.log('[Auth] PASSWORD_RECOVERY event detected, setting recovery flow');
          setIsRecoveryFlow(true);
          setActiveView('resetPassword');
          
          const flowState = authNavigationController.getState();
          if (flowState.type !== 'recovery') {
            authNavigationController.startFlow('recovery', session.user.email);
            authNavigationController.updateStage(AuthFlowStage.TOKEN_VERIFIED);
          }
          
          localStorage.setItem('lamulamu_in_recovery', 'true');
          
          // Temporarily disabled verification tracking
          // recordVerificationStep("password_recovery_event_detected", "info", {
          //   eventType: event,
          //   userId: session.user.id,
          //   email: session.user.email
          // });
        }
      });
      
      return () => {
        subscription.unsubscribe();
      };
    };
    
    // Temporarily disabled verification tracking
    // checkForAuthEvents();
  }, []);

  const extractParams = () => {
    let token = searchParams.get('token') || searchParams.get('code');
    let type = searchParams.get('type');
    let view = searchParams.get('view');
    let accessToken = null;
    
    if (window.location.hash) {
      try {
        console.log('[Auth] Hash fragment detected:', window.location.hash);
        
        if (window.location.hash.includes('access_token=')) {
          console.log('[Auth] Hash contains access_token, treating as recovery token');
          accessToken = window.location.hash.split('access_token=')[1]?.split('&')[0];
          if (accessToken) {
            token = token || accessToken;
            type = type || 'recovery';
            view = view || 'resetPassword';
            console.log('[Auth] Extracted access_token from hash:', {
              tokenPresent: !!token,
              type,
              view
            });
            
            if (type === 'recovery') {
              setShowAccessTokenDialog(true);
              setActiveView('resetPassword');
              setIsRecoveryFlow(true);
              
              authNavigationController.startFlow('recovery', null, token);
              authNavigationController.updateStage(AuthFlowStage.TOKEN_VERIFIED);
              
              localStorage.setItem('lamulamu_in_recovery', 'true');
            }
          }
        } else {
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          token = token || hashParams.get('token') || hashParams.get('code');
          type = type || hashParams.get('type');
          view = view || hashParams.get('view');
          
          console.log('[Auth] Extracted parameters from hash fragment:', {
            tokenPresent: !!token,
            type,
            view,
            hashFragment: window.location.hash
          });
        }
      } catch (error) {
        console.error('[Auth] Error parsing hash fragment:', error);
      }
    }
    
    return { token, type, view, accessToken };
  };

  const handleSignIn = async (formEmail: string, formPassword: string, e: React.FormEvent) => {
    e.preventDefault();
    if (!formEmail || !formPassword) {
      setValidationError("Email and password are required");
      return;
    }
    
    // Temporarily disabled verification tracking during normal login flows
    // if (testingMode) {
    //   const sessionId = await startVerificationSession(formEmail, 'sign_in', { email: formEmail }, 'Account Set Up - Standard. Login');
    //   console.log(`[DEBUG-Auth] Started sign-in session ${sessionId} for ${formEmail}`);
    //   if (sessionId) {
    //     await recordVerificationStep("sign_in_started", "info", { email: formEmail }, sessionId);
    //   }
    // }
    
    authNavigationController.startFlow('signin', formEmail);
    
    try {
      await signIn(formEmail, formPassword);
    } catch (error) {
      console.error("Sign in error:", error);
      authNavigationController.updateStage(AuthFlowStage.ERROR);
    }
  };

  const handleSignUp = async (formEmail: string, formPassword: string, formConfirmPassword: string, formDisplayName: string, e: React.FormEvent) => {
    e.preventDefault();
    console.log("[Auth] Starting signup process with:", { email: formEmail, displayName: formDisplayName });
    
    if (!formEmail || !formPassword || !formConfirmPassword || !formDisplayName) {
      setValidationError("All fields are required");
      return;
    }
    
    if (formDisplayName.length < 3) {
      setValidationError("Display name must be at least 3 characters long");
      return;
    }
    
    if (formPassword !== formConfirmPassword) {
      setValidationError("Passwords do not match");
      return;
    }
    
    // Check if this is a vendor invitation
    const isVendorInvite = searchParams.get('invite') === 'vendor';
    const invitePhone = searchParams.get('phone');
    console.log("[Auth] Invitation context:", { isVendorInvite, invitePhone });
    
    // Temporarily disabled verification tracking during signup
    // if (testingMode) {
    //   const sessionId = await startVerificationSession(formEmail, 'sign_up', { email: formEmail, displayName: formDisplayName }, 'Account Set Up - Standard. Create Account');
    //   console.log(`[DEBUG-Auth] Started sign-up session ${sessionId} for ${formEmail}`);
    //   if (sessionId) {
    //     await recordVerificationStep("sign_up_started", "info", { email: formEmail, displayName: formDisplayName }, sessionId);
    //   }
    // }
    
    authNavigationController.startFlow('signup', formEmail);
    
    try {
      await signUp(formEmail, formPassword, formConfirmPassword, formDisplayName);
      
      // Note: SMS invitation claiming is now handled in useSMSSignUp hook for the smsSignUp view
      // Regular signup flow doesn't handle SMS invitations
    } catch (error) {
      console.error("Sign up error:", error);
      authNavigationController.updateStage(AuthFlowStage.ERROR);
    }
  };

  const handleForgotPassword = async (formEmail: string, e: React.FormEvent) => {
    e.preventDefault();
    if (!formEmail) {
      setValidationError("Email is required");
      return;
    }
    
    // Temporarily disabled verification tracking during password reset
    // if (testingMode) {
    //   const sessionId = await startVerificationSession(formEmail, 'password_reset', { email: formEmail }, 'Password Reset - Standard. Request Reset');
    //   console.log(`[DEBUG-Auth] Started password reset session ${sessionId} for ${formEmail}`);
    //   if (sessionId) {
    //     await recordVerificationStep("password_reset_started", "info", { email: formEmail }, sessionId);
    //   }
    // }
    
    authNavigationController.startFlow('recovery', formEmail);
    
    try {
      await resetPassword(formEmail);
    } catch (error) {
      console.error("Password reset error:", error);
      authNavigationController.updateStage(AuthFlowStage.ERROR);
    }
  };

  const handleResetPassword = async (formPassword: string, e: React.FormEvent) => {
    e.preventDefault();
    if (!formPassword) {
      setValidationError("Password is required");
      return;
    }
    
    // Temporarily disabled verification tracking during password reset completion
    // if (testingMode) {
    //   const sessionId = await startVerificationSession(null, 'password_reset_completion', {}, 'Password Reset - Standard. Set New Password');
    //   console.log(`[DEBUG-Auth] Started password reset completion session ${sessionId}`);
    //   if (sessionId) {
    //     await recordVerificationStep("password_reset_completion_started", "info", {}, sessionId);
    //   }
    // }
    
    try {
      const { token, accessToken } = extractParams();
      const effectiveToken = token || accessToken;
      
      if (effectiveToken) {
        console.log(`[AUTH] Found token in URL or hash: ${effectiveToken ? 'present' : 'not present'}, storing in controller state`);
        const flowState = authNavigationController.getState();
        if (flowState.type === 'recovery' || !flowState.type) {
          authNavigationController.startFlow('recovery', null, effectiveToken);
          authNavigationController.updateStage(AuthFlowStage.TOKEN_VERIFIED);
        }
      }
      
      setShowAccessTokenDialog(false);
      
      await updatePassword(formPassword);
      
      console.log("[AUTH] Password update successful, letting controller handle navigation");
      
      localStorage.removeItem('lamulamu_in_recovery');
      
      setActiveView('signIn');
      
      toast({
        title: "Password updated successfully",
        description: "You can now sign in with your new password",
      });
    } catch (error) {
      console.error("Password update error:", error);
      authNavigationController.updateStage(AuthFlowStage.ERROR);
    }
  };

  const handleBackToSignIn = () => {
    localStorage.removeItem('lamulamu_in_recovery');
    setIsRecoveryFlow(false);
    setActiveView('signIn');
  };

  useEffect(() => {
    // Extract invitation parameters from URL
    const nameParam = searchParams.get('name');
    const phoneParam = searchParams.get('phone');
    const inviteParam = searchParams.get('invite');
    const viewParam = searchParams.get('view');
    
    console.log("[Auth] URL parameters detected:", { nameParam, phoneParam, inviteParam, viewParam });
    
    // Handle invitation parameters
    if (inviteParam === 'vendor' && nameParam && !displayName) {
      console.log("[Auth] Setting display name from URL parameter:", nameParam);
      setDisplayName(nameParam);
    }
    
    // Set view based on URL parameter - prioritize view parameter
    if (viewParam === 'smsSignUp') {
      console.log("[Auth] SMS signup view requested via URL parameter");
      setActiveView('smsSignUp');
    } else if (viewParam === 'signUp') {
      console.log("[Auth] Regular signup view requested via URL parameter");
      setActiveView('signUp');
    }
    
    // Debug logging for view state
    console.log("[Auth] Current activeView after URL processing:", activeView, "viewParam:", viewParam);
    
    const storedRecoveryFlow = localStorage.getItem('lamulamu_in_recovery');
    if (storedRecoveryFlow === 'true') {
      console.log('[Auth] Detected recovery flow from localStorage');
      setIsRecoveryFlow(true);
      setActiveView('resetPassword');
      
      const flowState = authNavigationController.getState();
      if (flowState.type !== 'recovery') {
        authNavigationController.startFlow('recovery', null);
        authNavigationController.updateStage(AuthFlowStage.TOKEN_VERIFIED);
      }
    }
    
    if (isAuthenticated && !isLoading) {
      setShowAccessTokenDialog(false);
      
      if (isRecoveryFlow || activeView === ('resetPassword' as AuthViewType)) {
        console.log("[AUTH] Recovery flow or reset password view detected, staying on this page");
        return;
      }
      
      const flowState = authNavigationController.getState();
      console.log("[AUTH] Authentication detected, current flow state:", flowState);
      
      if (flowState.type === 'recovery' && 
          (flowState.stage === AuthFlowStage.TOKEN_VERIFIED || 
           activeView === ('resetPassword' as AuthViewType))) {
        console.log("[AUTH] Recovery flow with token detected, staying on reset password view");
        return;
      }
      
      if (searchParams.has('type') && searchParams.get('type') === 'signup') {
        console.log("[AUTH] Signup parameter detected in URL, ensuring flow type is set correctly");
        if (!flowState.type || flowState.type !== 'signup') {
          console.log("[AUTH] Flow type is not signup, setting it explicitly");
          authNavigationController.startFlow('signup');
          authNavigationController.updateStage(AuthFlowStage.COMPLETED);
          authNavigationController.forceSaveState();
          console.log("[AUTH] Redirecting to terms of service as part of signup flow");
          navigate('/terms-of-service');
          return;
        }
      }
      
      if (flowState.type) {
        console.log("[AUTH] Active auth flow detected, deferring navigation to controller");
        return;
      }
      
      if (!flowState.type) {
        const checkVendorAndRedirect = async () => {
          try {
            const { data: userData } = await supabase.auth.getUser();
            if (userData?.user) {
              const { data: hasVendorRole, error: roleError } = await supabase.rpc('has_role', {
                '_role': 'vendor'
              });
              
              if (roleError) {
                console.error("Error checking vendor role:", roleError);
                const from = location.state?.from || "/";
                navigate(from);
                return;
              }
              
              if (hasVendorRole) {
                console.log("[AUTH] Vendor user detected, redirecting to vendor dashboard");
                navigate('/vendor');
                return;
              }
              
              if (userData.user.user_metadata?.role === 'admin') {
                console.log("[AUTH] Admin user detected, redirecting to admin dashboard");
                navigate('/admin');
                return;
              }
            }
            
            const from = location.state?.from || "/";
            console.log("[AUTH] Standard sign-in with no active flow, redirecting to:", from);
            navigate(from);
          } catch (error) {
            console.error("Error checking vendor status:", error);
            const from = location.state?.from || "/";
            navigate(from);
          }
        };
        
        checkVendorAndRedirect();
      }
    }
  }, [isAuthenticated, isLoading, navigate, location.state, searchParams, isRecoveryFlow, displayName]);

  useEffect(() => {
    console.log('[Auth] Component mounted, checking for auth tokens in URL or hash');
    const { token, type, view, accessToken } = extractParams();
    
    console.log('[Auth] URL/hash parameters check:', {
      token: token ? 'present' : 'none',
      accessToken: accessToken ? 'present' : 'none',
      type,
      view,
      timestamp: new Date().toISOString(),
      hash: window.location.hash ? 'present' : 'none',
      url: window.location.href
    });
    
    if ((token || accessToken) && (type === 'recovery' || type === 'recover')) {
      console.log('[Auth] Detected recovery token, setting view to resetPassword');
      setActiveView('resetPassword');
      setIsRecoveryFlow(true);
      
      authNavigationController.startFlow('recovery', null, token || accessToken);
      authNavigationController.updateStage(AuthFlowStage.TOKEN_VERIFIED);
      
      localStorage.setItem('lamulamu_in_recovery', 'true');
      
      if (accessToken) {
        setShowAccessTokenDialog(true);
      }
      
      // Temporarily disabled verification tracking
      // recordVerificationStep("auto_set_view_to_reset_password", "info", { 
      //   token: 'present',
      //   type,
      //   source: 'token_detection'
      // });
    } else if (view) {
      if (view === "resetPassword") {
        setActiveView("resetPassword" as AuthViewType);
        console.log("[Auth] Reset password view activated via URL parameter");
        
        const inRecovery = localStorage.getItem('lamulamu_in_recovery');
        if (inRecovery === 'true') {
          setIsRecoveryFlow(true);
        }
      } else if (view === "forgotPassword") {
        setActiveView("forgotPassword");
      } else if (view === "signUp") {
        setActiveView("signUp");
      } else if (view === "smsSignUp") {
        setActiveView("smsSignUp");
      } else {
        setActiveView("signIn");
      }
    }
  }, [searchParams, invitationToken]);

  useEffect(() => {
    console.log("[DEBUG-Auth] Auth page URL parameters:", {
      token: searchParams.has("token") ? "present" : "not present",
      type: searchParams.get("type"),
      all: Object.fromEntries(searchParams.entries()),
      hash: window.location.hash ? 'present' : 'none',
      fullUrl: window.location.href
    });
    
    const fetchAdminSettings = async () => {
      try {
        const { enabled, email } = await isTestingModeEnabled();
        setTestingMode(enabled);
        setTestEmail(email || "");
        
        if (enabled && email) {
          console.log('[DEBUG-Auth] Test mode active with email:', email);
          setEmail(email);
        }
      } catch (err) {
        console.error("[DEBUG-Auth] Error fetching admin settings:", err);
      }
    };

    fetchAdminSettings();
  }, [searchParams]);

  const getTitleAndDescription = () => {
    switch (activeView) {
      case 'signIn':
        return {
          title: "Sign in to your account",
          description: "Enter your credentials to access your account"
        };
      case 'signUp':
        return {
          title: "Create your account",
          description: "Fill in your details to create a new account"
        };
        case 'smsSignUp':
          return {
            title: "Complete Your Invitation",
            description: "Fill in your details to complete your account setup"
          };
      case 'forgotPassword':
        return {
          title: "Reset your password",
          description: "We'll send you a link to reset your password"
        };
      case 'resetPassword':
        return {
          title: "Set a new password",
          description: "Enter your new password below"
        };
      default:
        return {
          title: "Authentication",
          description: "Please complete the authentication process"
        };
    }
  };

  const { title, description } = getTitleAndDescription();

  const isProcessingToken = searchParams.has('token') || 
                           searchParams.has('code') || 
                           (window.location.hash && 
                            (window.location.hash.includes('token=') || 
                             window.location.hash.includes('code=') ||
                             window.location.hash.includes('access_token=')));

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      {isProcessingToken && !isRecoveryFlow ? (
        <div className="flex flex-col items-center justify-center p-6 bg-white rounded-lg shadow-md">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <h2 className="text-xl font-semibold">Verifying your email...</h2>
          <p className="text-gray-500 mt-2">Please wait while we process your verification</p>
        </div>
      ) : (
        <>
          {activeView === 'smsSignUp' ? (
            // SMS SignUp uses its own card design, not wrapped in AuthCard
            <div className="w-full max-w-md">
              <SMSSignUpForm
                email={email}
                setEmail={setEmail}
                password={password}
                setPassword={setPassword}
                confirmPassword={confirmPassword}
                setConfirmPassword={setConfirmPassword}
                displayName={displayName}
                setDisplayName={setDisplayName}
                loading={smsLoading}
                onSubmit={(email, password, confirmPassword, displayName, e) => {
                  const phoneParam = searchParams.get('phone') || '';
                  const inviteParam = searchParams.get('invite') || 'vendor';
                  const token = searchParams.get('token');
                  handleSMSSignUp(email, password, confirmPassword, displayName, phoneParam, inviteParam, token, e);
                }}
                validationError={smsValidationError}
                setValidationError={setSMSValidationError}
                phoneNumber={searchParams.get('phone') || undefined}
                inviteRole={searchParams.get('invite') || 'vendor'}
              />
            </div>
          ) : (
            <AuthCard 
              activeView={activeView} 
              setActiveView={setActiveView}
              title={title}
              description={description}
            >
              {isLoading && authStatus !== "error" && (
                <div className="flex justify-center items-center p-4">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <span className="ml-2">Processing...</span>
                </div>
              )}
                
              {activeView === 'signIn' && (
            <SignInForm
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              loading={isLoading}
              onSubmit={handleSignIn}
              onSignUp={() => setActiveView('signUp')}
              onForgotPassword={() => setActiveView('forgotPassword')}
              validationError={validationError}
              setValidationError={setValidationError}
            />
              )}

              {!isLoading && activeView === 'signUp' && (
                <SignUpForm
                  email={email}
                  setEmail={setEmail}
                  password={password}
                  setPassword={setPassword}
                  confirmPassword={confirmPassword}
                  setConfirmPassword={setConfirmPassword}
                  displayName={displayName}
                  setDisplayName={setDisplayName}
                  loading={isLoading}
                  onSubmit={handleSignUp}
                  validationError={validationError}
                  setValidationError={setValidationError}
                />
              )}
              
              {!isLoading && activeView === 'phoneSignUp' && (
                <PhoneSignUpForm
                  onBackToSignIn={() => setActiveView('signIn')}
                />
              )}

              {!isLoading && activeView === 'phoneSignUp' && (
                <PhoneSignUpForm
                  onBackToSignIn={() => setActiveView('signIn')}
                />
              )}

              {!isLoading && activeView === 'forgotPassword' && (
                <ForgotPasswordForm
                  email={email}
                  setEmail={setEmail}
                  loading={isLoading}
                  onSubmit={handleForgotPassword}
                  validationError={validationError}
                />
              )}
              
              {!isLoading && activeView === 'resetPassword' && (
                <ResetPasswordForm
                  password={password}
                  setPassword={setPassword}
                  loading={isLoading}
                  onSubmit={handleResetPassword}
                  validationError={validationError}
                />
              )}
            </AuthCard>
          )}
              
          {testingMode && testEmail && (
            <div className="w-full max-w-md mt-6">
              <h2 className="text-lg font-semibold text-center mb-2">Testing Mode Enabled</h2>
              <p className="text-sm text-center mb-4 text-gray-500">
                Verification and password reset emails for {testEmail} will appear below instead of being sent
              </p>
              <TestEmailViewer email={testEmail} />
            </div>
          )}
          
          <SafeAuthVerificationTracker />
          
          <Dialog open={showAccessTokenDialog} onOpenChange={setShowAccessTokenDialog}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Reset Your Password</DialogTitle>
                <DialogDescription>
                  We've detected that you're trying to reset your password. Please enter your new password below.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <p className="text-sm text-muted-foreground">
                  Your password reset link has been recognized. For security reasons, you need to set a new password now.
                </p>
                {activeView === 'resetPassword' && (
                  <ResetPasswordForm
                    password={password}
                    setPassword={setPassword}
                    loading={isLoading}
                    onSubmit={handleResetPassword}
                    validationError={validationError}
                  />
                )}
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
};

export default Auth;
